'use client'

import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import type {
  StyliticsComposerInstance,
  StyliticsOutfit,
  StyliticsOutfitClickEvent,
} from '@src/components/blocks/stylitics-recommendation-ds/stylitics.types'
import BottomDrawer from '@src/components/bottom-drawer'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Modal } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import type Product from '@src/quince-site-common/src/domains/Product'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { buildStyliticsProductProperties } from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/stylitics-product-properties'
import StyliticsProductFeedDesktop from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/StyliticsProductFeedDesktop'
import StyliticsProductFeedMobile from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/StyliticsProductFeedMobile'
import styles from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/styliticsRecommendationModule.module.scss'
import {
  getItems,
  initializeStyliticsComposer,
  STYLITICS_CONFIG,
  StyliticsModalTrackingConfig,
  trackStyliticsWidgetImpressions,
} from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/utils'
import { getColorwayId } from '@src/telemetry/integrations/provider-utils/stylitics-utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

export interface StyliticsRecommendationPlLegacyProps {
  /** The `protoToProduct`-adapted buy-box product (resolved by the router in `index.tsx`). */
  product?: Product
  /** The selected color from the PL selection bus (resolved by the router in `index.tsx`). */
  selectedColor?: string
  /** The Shop-the-Look reco band, rendered when Stylitics has no outfits / fails to load. */
  fallback: React.ReactNode
  /**
   * Whether the page carries buy-box data at all (either PRODUCT_PURCHASE widget). `false` means
   * there is no product to key Stylitics off — the PL equivalent of the non-PL module's bundle
   * branch (`showStyliticsModule === false`), which returns PairWithProducts and mounts no
   * composer host. Distinguishes that from "buy box present, selection not resolved yet".
   */
  hasPurchaseData: boolean
}

/**
 * Legacy-styled (non-DS) presentation of the PL Stylitics widget — the SCSS-module render the
 * non-DS PL PDP serves, sharing the legacy module's markup, styles, telemetry, and outfit
 * modal/drawer. The DS PL path renders `StyliticsRecommendationDS` instead (routed in
 * `index.tsx`). Data (product/color) arrives via props so this file stays a pure presentation +
 * composer-lifecycle fork.
 */
const StyliticsRecommendationPlLegacy = ({
  product,
  selectedColor,
  fallback,
  hasPurchaseData,
}: StyliticsRecommendationPlLegacyProps) => {
  const lbUserId = getlbUserId()
  const [isStyliticsDataPresent, setIsStyliticsDataPresent] = useState<boolean>(false)
  const [isStyliticsLoading, setIsStyliticsLoading] = useState<boolean>(true)
  const [customProductFeedType, setCustomProductFeedType] = useState<null | string>(null)
  const [currentOutfitData, setCurrentOutfitData] = useState<StyliticsOutfit | null>(null)
  const [allOutfitsData, setAllOutfitsData] = useState<StyliticsOutfit[] | null>(null)
  const [initialSlide, setInitialSlide] = useState<number | null>(-1)
  const { t } = useTranslation()
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const itemNumber = getColorwayId(product?.spid.toString(), selectedColor)
  const styliticsWidgetRef = useRef<HTMLDivElement>(null)

  const productProperties = buildStyliticsProductProperties(product, selectedColor)

  const overrideOnClick = (e: StyliticsOutfitClickEvent) => {
    setCurrentOutfitData(e.outfit)
    setInitialSlide(e.outfit.index)
    if (isMobile) {
      setCustomProductFeedType('bottom_drawer')
    } else {
      setCustomProductFeedType('desktop_modal')
    }

    trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
      action: 'visible_product_list',
      //eslint-disable-next-line i18next/no-literal-string
      entity_type: 'product_card_view',
      context: {
        productProperties: productProperties,
        product_count: e.outfit.items?.length,
        ...StyliticsModalTrackingConfig,
        items: getItems(e.outfit) as unknown,
      },
    })
  }

  const destroyStyliticsComposer = async (composerInstance?: StyliticsComposerInstance) => {
    if (composerInstance) {
      await composerInstance.destroy?.()
    }
  }

  useEffect(() => {
    // Product resolved but no colorway id yet (no color axis, or the selection bus has not
    // published one) → nothing to key the composer off, so end the loading state and let the
    // fallback render. Parity with non-PL, where `initializeStyliticsComposer` takes the same
    // `!selectedColor` early-return. The no-buy-box case never reaches here — it returns the bare
    // fallback above, mirroring the non-PL bundle branch.
    if (!itemNumber) {
      setIsStyliticsLoading(false)
      return
    }
    setIsStyliticsLoading(true)
    setIsStyliticsDataPresent(false)
    let composerInstance: StyliticsComposerInstance | undefined
    initializeStyliticsComposer({
      selectedColor,
      itemNumber,
      overrideOnClick,
      setAllOutfitsData,
      setIsStyliticsDataPresent,
      setIsStyliticsLoading,
      lbUserId,
      t,
      productProperties,
    })
      .then((composer: unknown) => {
        if (composer) {
          composerInstance = composer as StyliticsComposerInstance
        } else {
          // Composer never created (script blocked / failed to load) — unblock the band fallback
          // by ending the loading state. The `'load.data'` handler never fires in this path.
          setIsStyliticsLoading(false)
        }
      })
      .catch((error: unknown) => {
        captureException(error, {
          module: MODULES.STYLITICS_RECOMMENDATION,
          feature: FEATURES.LOAD_STYLITICS_COMPOSER,
        })
        console.error('Error initializing Stylitics Composer:', error)
        setIsStyliticsLoading(false)
      })
    return () => {
      void destroyStyliticsComposer(composerInstance)
    }
  }, [selectedColor, itemNumber])

  useEffect(() => {
    if (styliticsWidgetRef.current && isStyliticsDataPresent) {
      trackStyliticsWidgetImpressions(styliticsWidgetRef, allOutfitsData, productProperties)
    }
  }, [styliticsWidgetRef, isStyliticsDataPresent, allOutfitsData])

  // No buy box on the page → render ONLY the fallback band, with no collapsed composer host in the
  // DOM. Mirrors the non-PL module's bundle branch (`if (!showStyliticsModule) return
  // <PairWithProducts />`), which likewise emits no Stylitics markup rather than an empty
  // collapsed wrapper.
  if (!hasPurchaseData) return <>{fallback}</>

  return (
    <>
      <div
        // Section locator parity with the DS fork and the legacy module; gated like the title so
        // the id never coexists with the fallback band's identical shop-the-look testid.
        data-testid={isStyliticsDataPresent ? TEST_LOCATORS.PDP_SECTIONS.SHOP_THE_LOOK : undefined}
        className={classNames(styles.stylitics_wrapper, styles.stylitics_wrapper__ctlAboveMte, {
          [styles.stylitics_wrapper__collapsed ?? '']: !isStyliticsDataPresent,
        })}
      >
        {isStyliticsDataPresent && (
          <h3 id="stylitics-widget-title" className={styles.stylitics_wrapper__title}>
            {t('shop_the_look')}
          </h3>
        )}
        <div
          ref={styliticsWidgetRef}
          id={STYLITICS_CONFIG.TARGET}
          data-testid={TEST_LOCATORS.PDP_SECTIONS.STYLITICS_WIDGET_TARGET}
        ></div>
      </div>
      {isMobile && currentOutfitData ? (
        <BottomDrawer
          show={customProductFeedType === 'bottom_drawer'}
          onClose={() => {
            setCustomProductFeedType(null)
            setCurrentOutfitData(null)
            setInitialSlide(null)
          }}
          header={t('shop_the_look')}
          className={styles.styliticsProductFeedMobile__bottomSheetDrawer}
          headerClassName={styles.styliticsProductFeedMobile__bottomSheetHeader}
          bodyClassName={styles.styliticsProductFeedMobile__bottomSheetBody}
        >
          <StyliticsProductFeedMobile
            productProperties={productProperties}
            data={currentOutfitData}
            customProductFeedType={customProductFeedType}
          />
        </BottomDrawer>
      ) : (
        <Modal
          open={customProductFeedType === 'desktop_modal'}
          onClose={() => {
            setCustomProductFeedType(null)
            setCurrentOutfitData(null)
            setInitialSlide(null)
          }}
          className={styles.styliticsProductFeedDesktop__wrapper}
          showCloseButton={true}
        >
          <StyliticsProductFeedDesktop
            productProperties={productProperties}
            data={allOutfitsData}
            initialSlide={initialSlide}
            customProductFeedType={customProductFeedType}
          />
        </Modal>
      )}
      {!isStyliticsLoading && !isStyliticsDataPresent && fallback}
    </>
  )
}

export default StyliticsRecommendationPlLegacy
